import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  getBrowserPrintConfig, setBrowserPrintConfig, listBrowserPrintPrinters, sendViaBrowserPrint,
} from '@/utils/browserPrint';
import { buildStickerEPL } from '@/utils/usbPrinter';

const SAMPLE_JOB = { job_number: 'TEST', customer_name: 'Test Sticker', mobile: '0400 000 000' };

export const BrowserPrintSettings = ({ settings }) => {
  const [cfg, setCfg] = useState(getBrowserPrintConfig);
  const [printers, setPrinters] = useState([]);
  const [busy, setBusy] = useState(false);

  const save = (next) => { setBrowserPrintConfig(next); setCfg(next); };

  const detect = async () => {
    setBusy(true);
    try {
      const list = await listBrowserPrintPrinters();
      setPrinters(list);
      if (!list.length) toast.info('Browser Print is running but found no printers');
      else toast.success(`Found ${list.length} printer(s)`);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const testPrint = async () => {
    setBusy(true);
    try {
      await sendViaBrowserPrint(cfg.device, buildStickerEPL(SAMPLE_JOB, settings));
      toast.success(`Test sticker sent to ${cfg.device.name}`);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div data-testid="browser-print-settings" style={{ padding: '1rem', border: '1px solid #bfdbfe', borderRadius: '0.5rem', backgroundColor: '#eff6ff' }}>
      <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>Zebra Browser Print — this PC (Recommended)</h4>
      <p style={{ fontSize: '0.75rem', color: '#475569', marginBottom: '0.75rem' }}>
        Sends stickers straight to the chosen Zebra with no print dialog, so it never jumps to another printer.
        Works with USB or shared/mapped Zebras. Set up once on <strong>each PC</strong>: install{' '}
        <a href="https://www.zebra.com/us/en/support-downloads/software/printer-software/browser-print.html" target="_blank" rel="noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>Zebra Browser Print</a>,
        click Detect, pick the Zebra, then tick Enable. If Chrome or Browser Print asks for permission, click Allow.
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <Button type="button" variant="outline" onClick={detect} disabled={busy} data-testid="browser-print-detect-button">Detect Printers</Button>
        <select
          value={cfg.device?.uid || ''}
          onChange={(e) => save({ ...cfg, device: printers.find((p) => p.uid === e.target.value) || null })}
          disabled={!printers.length}
          style={{ height: '2.25rem', border: '1px solid #cbd5e1', borderRadius: '0.375rem', padding: '0 0.5rem', minWidth: '220px' }}
          data-testid="browser-print-printer-select"
        >
          {cfg.device && !printers.some((p) => p.uid === cfg.device.uid) && <option value={cfg.device.uid}>{cfg.device.name}</option>}
          {!cfg.device && <option value="">{printers.length ? 'Select a Zebra…' : 'Click Detect first'}</option>}
          {printers.map((p) => <option key={p.uid} value={p.uid}>{p.name} ({p.connection})</option>)}
        </select>
        <Button type="button" variant="outline" onClick={testPrint} disabled={busy || !cfg.device} data-testid="browser-print-test-button">Test Print</Button>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={!!cfg.enabled}
            disabled={!cfg.device}
            onChange={(e) => save({ ...cfg, enabled: e.target.checked })}
            data-testid="browser-print-enable-checkbox"
          />
          Enable on this PC
        </label>
      </div>
    </div>
  );
};
